<template>
  <div v-if="haveBooking && havePaymentViewing && canShowPayments">
    <payment-single-success
      v-if="paymentViewingIsPaid || paymentViewingIsOnHold"
      :payment-viewing="paymentViewing"/>
    <template v-else>
      <div class="dashboard-page-title">
        <div class="container-fluid" style="max-width: 630px;">
          <h1 class="text-center" style="margin-top: 50px;">Complete Payment</h1>
        </div>
      </div>

      <div class="container-fluid">
        <div class="dashboard-block">
          <div class="row">
            <div class="col-sm-offset-1 col-sm-10 col-md-offset-2 col-md-8 col-lg-offset-2 col-lg-8">

              <div class="payment-container">

                <div class="text-center payment-alert">
                  <div v-if="this.bookingPaymentRequiresBoth || this.bookingPaymentDepositIsUnpaid" class="h4" >
                      <p>Thanks for booking a sports {{ $starLabel }}!</p>
                      <p>To confirm your booking, please pay your deposit as soon as possible.</p>
                      <br/>
                      <p>Or review your
                        <router-link style="text-decoration: underline" active-class="" :to="{ name: 'bookings.show', params: { id: this.bookingId } }">
                          <a>booking</a>
                        </router-link>
                      </p>
                  </div>
                  <div v-else class="h4" >
                    <p>You're almost there!</p>
                    <p>Please pay the outstanding amount to finalise your booking.</p>
                    <br/>
                    <p>Or review your
                      <router-link style="text-decoration: underline" active-class="" :to="{ name: 'bookings.show', params: { id: this.bookingId } }">
                        <a>booking</a>
                      </router-link>
                    </p>
                  </div>
                </div>

                <div class="form-horizontal">

                  <div class="form-group payment-details">
                    <div class="col-sm-4">
                      <p class="control-label">Payment</p>
                    </div>
                    <div class="col-sm-8">
                      <p class="detail">Booking #{{ booking.id | padded('0', 5) }}</p>
                    </div>

                    <div class="clearfix m-b-sm"></div>

                    <div class="col-sm-4">
                      <p class="control-label label-amount">Amount</p>
                    </div>
                    <div class="col-sm-8">
                      <selectize
                        name="payment_amount"
                        class="selectize selectize-single form-control"
                        :disable-delete="true"
                        :options="paymentAmountOptions"
                        v-model="statePaymentIds"/>
                    </div>
                    <div v-if="this.bookingPaymentRequiresBoth" style="text-align: center">
                      <p class="text-danger"><i class="fa fa-info-circle" aria-hidden="true"></i>&nbsp; As your booking is less than 14 days away, we require full payment</p>
                    </div>
                  </div>

                  <div class="decorative-divider"></div>

                  <div :class="['form-group', errorClass('payment_method')]">
                    <label for="payment-method" class="col-sm-4 control-label">Payment Method</label>
                    <div class="col-sm-8">
                      <selectize
                        id="payment-method"
                        name="payment_method"
                        class="selectize selectize-single form-control"
                        :disable-delete="true"
                        :options="enabledPaymentMethodOptions"
                        v-model="paymentMethod"/>
                      <help-block field="payment_method"/>
                    </div>
                  </div>

                  <payment-single-credit-card
                    v-if="paymentMethodIsCreditCard"
                    :payment-ids="paymentIds"
                    :payment-amount="paymentAmount"
                    :card-surcharge="cardSurcharge"
                  />

                  <payment-single-bank-transfer
                    v-if="paymentMethodIsBankTransfer"
                    :payment-ids="paymentIds"
                  />

                  <payment-single-cheque
                    v-if="paymentMethodIsCheque"
                    :payment-ids="paymentIds"
                  />

                  <payment-single-existing-credit-card
                    v-if="paymentMethodIsExistingCreditCard"
                    :payment-ids="paymentIds"
                    :payment-amount="paymentAmount"
                    :card-surcharge="cardSurcharge"
                  />

                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import { mapActions, mapGetters, mapState } from 'vuex'
import { helpers } from 'mixins/bookings'
import { notify } from 'utils'
import { isArray, join, isNumber, isString, split, each } from 'lodash'
import PaymentSingleSuccess from './PaymentSingleSuccess'
import PaymentSingleCreditCard from './PaymentSingleCreditCard'
import PaymentSingleBankTransfer from './PaymentSingleBankTransfer'
import PaymentSingleCheque from './PaymentSingleCheque'
import PaymentSingleExistingCreditCard from './PaymentSingleExistingCreditCard'

import Selectize from '../ext/codium/CodiumSelectize'

export default {
  mixins: [helpers],

  props: {
    bookingId: {
      required: true
    },
    paymentId: {
      required: true
    },
    action: {
      type: String,
      required: true
    },
    showMessage: {
      type: Boolean,
      default: false
    }
  },

  components: {
    PaymentSingleSuccess,
    PaymentSingleExistingCreditCard,
    PaymentSingleCreditCard,
    PaymentSingleBankTransfer,
    PaymentSingleCheque,
    Selectize
  },

  data () {
    return {

      canShowPayments: false,
      paymentIds: [],
      paymentMethod: function () {
        return this.paymentMethod
      }
    }
  },

  computed: {
    ...mapState('booking', ['booking']),
    ...mapGetters('booking', ['haveBooking']),

    /**
     * Setter and getter for payment amounts.
     *
     * We want to store the payment amounts as an array, but Selectize requires our IDs to be strings, so
     * we'll store and retrieve it accordingly.
     *
     * @return object
     */
    statePaymentIds: {
      get () {
        if (isArray(this.paymentIds)) {
          return join(this.paymentIds)
        }

        return this.paymentIds
      },

      set (value) {
        if (isNumber(value)) {
          this.paymentIds = [value]
        } else if (isString('string')) {
          this.paymentIds = split(value, ',')
        } else {
          this.paymentIds = []
        }
      }
    },

    cardSurcharge: {
      get () {
        let total = 0
        if (this.paymentIds.length > 1) {
          // we use the booking total payment
          total = this.booking.payment_totals.card_surcharge
        } else {
          each(this.booking.payments, (payment) => {
            if (this.paymentIds.indexOf(payment.id) >= 0 && payment.card_surcharge) {
              total += payment.card_surcharge
            }
          })
        }
        return total
      }
    },

    paymentAmount: {
      get () {
        var total = 0
        each(this.booking.payments, (payment) => {
          if (this.statePaymentIds.indexOf(payment.id) >= 0) {
            total += payment.total_inclusive
          }
        })
        return total
      }
    },

    /**
     * Determine if we are viewing the deposit payment.
     *
     * @return bool
     */
    paymentViewingIsDeposit () {
      return this.bookingPaymentDeposit && this.bookingPaymentDeposit.id === parseInt(this.paymentId)
    },

    /**
     * Determine if we are viewing the final payment.
     *
     * @return bool
     */
    paymentViewingIsFinal () {
      return this.bookingPaymentFinal && this.bookingPaymentFinal.id === parseInt(this.paymentId)
    },

    /**
     * Determine if we are viewing the full payment.
     *
     * @return bool
     */
    paymentViewingIsFull () {
      return this.bookingPaymentFull && this.bookingPaymentFull.id === parseInt(this.paymentId)
    },

    /**
     * Get the booking payment we are currently viewing.
     *
     * @return object|null
     */
    paymentViewing () {
      if (this.paymentViewingIsDeposit) {
        return this.bookingPaymentDeposit
      }

      if (this.paymentViewingIsFinal) {
        return this.bookingPaymentFinal
      }

      if (this.paymentViewingIsFull) {
        return this.bookingPaymentFull
      }

      return null
    },

    /**
     * Determine if we have a payment to view.
     *
     * @return bool
     */
    havePaymentViewing () {
      return this.paymentViewing !== null
    },

    /**
     * Determine if we should be showing the success "thanks" screen based on whether
     * the selected payment is processing.
     *
     * @return bool
     */
    paymentViewingIsPaid () {
      if (this.paymentViewingIsDeposit && this.bookingPaymentDepositIsPaid) {
        return true
      }

      if (this.paymentViewingIsFinal && this.bookingPaymentFinalIsPaid) {
        return true
      }

      if (this.paymentViewingIsFull && this.bookingPaymentFullIsPaid) {
        return true
      }

      return false
    },

    paymentViewingIsOnHold () {
      return this.paymentViewing.status === 'hold'
    },

    paymentViewingIsPending () {
      if (this.paymentViewingIsDeposit && this.bookingPaymentDepositIsPending) {
        return true
      }

      if (this.paymentViewingIsFinal && this.bookingPaymentFinalIsPending) {
        return true
      }

      if (this.paymentViewingIsFull && this.bookingPaymentFullIsPending) {
        return true
      }

      return false
    },

    paymentViewingIsCardSent () {
      if (this.paymentViewingIsDeposit && this.bookingPaymentDepositIsPending) {
        return true
      }

      if (this.paymentViewingIsFinal && this.bookingPaymentFinalIsPending) {
        return true
      }

      if (this.paymentViewingIsFull && this.bookingPaymentFullIsPending) {
        return true
      }

      return false
    },

    /**
     * Opposite of above.
     *
     * @return bool
     */
    paymentViewingIsUnpaid () {
      return !this.paymentViewingIsPaid
    },

    /**
     * Determine if payment transaction method is credit card.
     *
     * @return bool
     */
    paymentMethodIsCreditCard () {
      return this.paymentMethod === 'credit_card'
    },

    /**
     * Determine if payment transaction method is is use existing credit card.
     *
     * @return bool
     */
    paymentMethodIsExistingCreditCard () {
      return this.paymentMethod === 'existing_card'
    },

    /**
     * Determine if payment transaction method is bank transfer.
     *
     * @return bool
     */
    paymentMethodIsBankTransfer () {
      return this.paymentMethod === 'bank_transfer'
    },

    /**
     * Determine if payment transaction method is bank transfer.
     *
     * @return bool
     */
    paymentMethodIsCheque () {
      return this.paymentMethod === 'cheque'
    },

    /**
     * The payment amount options.
     *
     * Depends on which payments have already been made for the booking.
     *
     * This applies tax to the payment amounts and shows the amount as including tax.
     *
     * @return array
     */
    paymentAmountOptions () {
      const currency = this.$options.filters.currency
      const bothPaymentsRequired = this.bookingPaymentRequiresBoth
      const depositPaid = this.bookingPaymentDepositIsPaid
      const finalPaid = this.bookingPaymentFinalIsPaid

      if (bothPaymentsRequired) {
        /**
         * First test if the full payment is required upfront
         * If the full amount ifs required, display a single option with the full invoice amount
         */
        return [
          {
            id: this.bothPaymentsId,
            text: `Deposit + Final: ${currency(this.bookingPaymentTotalAmount, this.bookingPaymentDeposit.currency)} (including tax)`
          }
        ]
      } else if (depositPaid && !finalPaid) {
        /**
         * If a deposit has been paid but the final payment is yet to be paid
         * returns the final amount outstanding on the invoice
         */
        return [
          {
            id: this.bookingPaymentFinal.id,
            text: `Final: ${currency(this.bookingPaymentFinal.total_inclusive, this.bookingPaymentFinal.currency)} (including tax)`
          }
        ]
      } else if (!depositPaid && finalPaid) {
        /**
         * If a deposit has been paid but the final payment is yet to be paid
         * returns the final amount outstanding on the invoice
         */
        return [
          {
            id: this.bookingPaymentDeposit.id,
            text: `Deposit: ${currency(this.bookingPaymentDeposit.total_inclusive, this.bookingPaymentDeposit.currency)} (including tax)`
          }
        ]
      } else if (this.paymentViewingIsFull) {
        return [
          {
            id: this.bookingPaymentFull.id,
            text: `Full Payment: ${currency(this.bookingPaymentFull.total_inclusive, this.bookingPaymentFull.currency)} (including tax)`
          }
        ]
      } else if (!depositPaid && !finalPaid) {
        /**
         * If neither a deposit or full payments has been made
         * returns an option to pay either the deposit or the full amount
         */
        return [
          {
            id: this.bookingPaymentDeposit.id,
            text: `Deposit: ${currency(this.bookingPaymentDeposit.total_inclusive, this.bookingPaymentDeposit.currency)} (including tax)`
          },
          {
            id: this.bothPaymentsId,
            text: `Deposit + Final: ${currency(this.bookingPaymentTotalAmount, this.bookingPaymentDeposit.currency)} (including tax)`
          }
        ]
      }
    },

    bothPaymentsId () {
      if (!this.bookingPaymentDeposit || !this.bookingPaymentFinal) {
        return null
      }
      return `${this.bookingPaymentDeposit.id},${this.bookingPaymentFinal.id}`
    }
  },

  created () {
    this.getBooking({ id: this.bookingId })
      .then(this.redirectIfBookingTermsAndConditionsNotAccepted)
      .then(this.redirectIfNotPaymentForBooking)
      .then(this.redirectToOtherActionIfRequired)
      .then(this.setDefaultSelectedPaymentIds)
      .then(() => {
        this.canShowPayments = true
      })
  },

  methods: {
    ...mapActions('booking', ['getBooking']),

    /**
     * Redirect with an error if the bookings terms and conditions have not been accepted.
     *
     * @return Promise
     */

    /**
     * Redirect with an error if the bookings terms and conditions have not been accepted.
     *
     * @return Promise
     */
    redirectIfBookingTermsAndConditionsNotAccepted () {
      if (this.bookingTermsAreAccepted || this.$userIsAdmin) {
        return Promise.resolve()
      }

      notify.error('Unable to make payments until terms and conditions are accepted.')

      this.$router.push({ name: 'bookings.show', params: { id: this.booking.id } })
    },

    /**
     * Redirect with an error if the payment does not belong to the booking.
     *
     * @return Promise
     */
    redirectIfNotPaymentForBooking () {
      if (this.havePaymentViewing) {
        return Promise.resolve()
      }

      notify.error('Unable to find payment for booking.')

      this.$router.push({
        name: 'bookings.show.payment',
        params: {
          id: this.booking.id
        },
        props: {
          showMessage: true
        }
      })
    },

    /**
     * Redirect to other payment action if required.
     *n
     * This is purely aesthetic and makes the URL look a lot cleaner when viewing a payment
     * versus processing one.
     *
     * @return Promise
     */
    redirectToOtherActionIfRequired () {
      if (this.action === 'process' && this.paymentViewingIsPaid) {
        this.$router.push({ name: 'payments.show', params: { action: 'view' } })
      } else if (this.action === 'view' && this.paymentViewingIsUnpaid) {
        this.$router.push({ name: 'payments.show', params: { action: 'process' } })
      }

      return Promise.resolve()
    },

    /**
     * Select the payment amount based on what ID was passed in.
     *
     * @return void
     */
    setDefaultSelectedPaymentIds () {
      // Select the "deposit" payment by default IF we are viewing the deposit directly and the
      // deposit is unpaid or sent.
      this.paymentMethod = this.paymentViewing.method

      if (this.bookingPaymentRequiresBoth) {
        this.statePaymentIds = this.bothPaymentsId
      } else if (this.paymentViewingIsDeposit && (this.bookingPaymentDepositIsUnpaid || this.bookingPaymentDepositIsSent)) {
        this.statePaymentIds = this.bookingPaymentDeposit.id
      } else if (this.paymentViewingIsFinal) {
        // Select both the "deposit" and the "deposit + final" payments IF we are viewing the final
        // and the deposit and final are both unpaid or they have both been sent.
        if (
          (this.bookingPaymentDepositIsUnpaid && this.bookingPaymentFinalIsUnpaid) ||
          (this.bookingPaymentDepositIsSent && this.bookingPaymentFinalIsSent)
        ) {
          this.statePaymentIds = this.bothPaymentsId

        // Select only the "final" payment IF we are viewing the final and the final payment is
        // either unpaid or sent.
        } else if (this.bookingPaymentFinalIsUnpaid || this.bookingPaymentFinalIsSent) {
          this.statePaymentIds = this.bookingPaymentFinal.id
        }
      } else if (this.paymentViewingIsFull) {
        this.statePaymentIds = this.bookingPaymentFull.id
      }
    }

  }
}
</script>
